
import { useState } from "react";
import Link from "@/components/ui/link-compat";
import type { AppColumnDef } from "@/lib/table-features";
import type { SuperAdminUser } from "@/types/superadmin";
import { textFilterFn } from "@/lib/table-filters";
import { formatDate } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LogIn, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function ImpersonateButton({ user, onImpersonate }: { user: SuperAdminUser; onImpersonate: (userId: string) => void }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)} className="gap-1">
        <LogIn className="h-3.5 w-3.5" />
        Login as
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogMedia className="bg-amber-100 dark:bg-amber-950">
              <LogIn className="size-8 text-amber-600 dark:text-amber-400" />
            </AlertDialogMedia>
            <AlertDialogTitle>Impersonate user?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <p>
                You will be logged in as <strong>{user.email}</strong>. Your current session will end.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onImpersonate(user.id)}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function DeleteUserButton({ user, onDelete }: { user: SuperAdminUser; onDelete: (userId: string) => void }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)} className="gap-1 text-destructive hover:text-destructive hover:bg-destructive/10">
        <Trash2 className="h-3.5 w-3.5" />
        Delete
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogMedia className="bg-red-100 dark:bg-red-950">
              <Trash2 className="size-8 text-red-600 dark:text-red-400" />
            </AlertDialogMedia>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <p>
                This will permanently delete <strong>{user.email}</strong> and all their data including owned organizations. This cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { onDelete(user.id); setOpen(false); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function getUserColumns(
  onImpersonate?: (userId: string) => void,
  onDelete?: (userId: string) => void,
): AppColumnDef<SuperAdminUser, unknown>[] {
  return [
    {
      accessorKey: "email",
      header: "Email",
      size: 200,
      cell: ({ row }) => (
        <Link href={`/dashboard/settings/admin/user?id=${row.original.id}`} prefetch={false} className="font-medium text-blue-600 hover:underline">
          {row.original.email}
        </Link>
      ),
      filterFn: textFilterFn,
    },
    {
      accessorKey: "firstName",
      header: "First Name",
      cell: ({ row }) => row.original.firstName || "-",
      filterFn: textFilterFn,
    },
    {
      accessorKey: "lastName",
      header: "Last Name",
      cell: ({ row }) => row.original.lastName || "-",
      filterFn: textFilterFn,
    },
    {
      accessorKey: "organizationCount",
      header: "Organizations",
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <Badge variant="secondary">{row.original.organizationCount}</Badge>
          {row.original.organizationNames.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {row.original.organizationNames.join(", ")}
            </span>
          )}
        </div>
      ),
    },
    {
      accessorKey: "isSuperAdmin",
      header: "Super Admin",
      size: 100,
      cell: ({ row }) =>
        row.original.isSuperAdmin ? (
          <Badge variant="destructive">Super Admin</Badge>
        ) : null,
    },
    {
      accessorKey: "createdAt",
      header: "Joined",
      size: 120,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm whitespace-nowrap">
          {formatDate(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      size: 160,
      cell: ({ row }) =>
        !row.original.isSuperAdmin ? (
          <div className="flex gap-1">
            {onImpersonate && <ImpersonateButton user={row.original} onImpersonate={onImpersonate} />}
            {onDelete && <DeleteUserButton user={row.original} onDelete={onDelete} />}
          </div>
        ) : null,
    },
  ];
}
